import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaTrash, FaImage, FaBell, FaCopy, FaUpload, FaPlus, FaTimes, FaThLarge } from 'react-icons/fa';
import './EditProduct.css';

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingVariantImage, setUploadingVariantImage] = useState({});
  const [showVariants, setShowVariants] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryLabels, setCategoryLabels] = useState({});
  const [sliderTitle, setSliderTitle] = useState('');
  const [sliderSubtitle, setSliderSubtitle] = useState('');
  const [savingSlider, setSavingSlider] = useState(false);
  const [product, setProduct] = useState({
    id: '',
    name: '',
    description: '',
    price: '',
    sale_price: '',
    category: 'electronics',
    stock: '',
    status: 'active',
    low_stock_threshold: '5',
    images: [],
    featured: 0,
    product_details_title: 'Product Details',
    product_details: '',
    fabric_care_title: 'Fabric & Care',
    fabric_care: '',
    delivery_info_title: 'Delivery Information',
    delivery_info: '',
    return_policy_title: 'Return & Exchange Policy',
    return_policy: '',
    slider_badge: '',
  });
  const [variants, setVariants] = useState([]);

  const [tagNewArrival, setTagNewArrival] = useState(false);
  const [tagSale, setTagSale] = useState(false);

  const loadCategories = async () => {
    try {
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/public/get-settings.php');
      const data = await response.json();

      if (data.success && data.settings) {
        let categoriesArray = [];
        let labelsObj = {};

        if (data.settings.categories) {
          try {
            const parsed = JSON.parse(data.settings.categories);
            categoriesArray = parsed;
          } catch (e) {
            console.error('Error parsing categories:', e);
          }
        }

        if (data.settings.category_labels) {
          try {
            const parsedLabels = JSON.parse(data.settings.category_labels);
            labelsObj = parsedLabels;
          } catch (e) {
            console.error('Error parsing category labels:', e);
          }
        }

        setCategories(categoriesArray);
        setCategoryLabels(labelsObj);
      } else {
        setCategories(['electronics', 'clothing', 'books', 'accessories', 'home', 'sports']);
        setCategoryLabels({
          electronics: 'Electronics',
          clothing: 'Clothing',
          books: 'Books',
          accessories: 'Accessories',
          home: 'Home & Living',
          sports: 'Sports & Outdoors'
        });
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories(['electronics', 'clothing', 'books', 'accessories', 'home', 'sports']);
      setCategoryLabels({
        electronics: 'Electronics',
        clothing: 'Clothing',
        books: 'Books',
        accessories: 'Accessories',
        home: 'Home & Living',
        sports: 'Sports & Outdoors'
      });
    }
  };

  const [guide, setGuide] = useState({
    guide_type: 'size_guide',
    guide_title: 'Size Guide',
    guide_description: '',
    guide_rows: [],
    guide_image: '',
    show_on_page: 1,
  });
  const [showGuideSection, setShowGuideSection] = useState(false);
  const [uploadingGuideImage, setUploadingGuideImage] = useState(false);
  const [savingGuide, setSavingGuide] = useState(false);

  const loadGuide = async () => {
    try {
      const response = await fetch(`https://demo-site.storii.com.pk/demo-api/admin/get-product-guide.php?product_id=${id}`);
      const data = await response.json();
      if (data.success && data.guide) {
        setGuide(data.guide);
      }
    } catch (error) {
      console.error('Error loading guide:', error);
    }
  };

  const loadSliderSettings = async () => {
    try {
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/public/get-settings.php');
      const data = await response.json();
      if (data.success && data.settings) {
        setSliderTitle(data.settings.homepage_slider_title || 'New Arrivals');
        setSliderSubtitle(data.settings.homepage_slider_subtitle || '');
      }
    } catch (error) {
      console.error('Error loading slider settings:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await loadCategories();
      await loadProduct();
      await loadVariants();
      await loadGuide();
      await loadSliderSettings();
    };
    loadData();
  }, [id]);

  const loadProduct = async () => {
    setLoading(true);
    try {
      const response = await fetch(`https://demo-site.storii.com.pk/demo-api/admin/get-product-simple.php?id=${id}`);
      const data = await response.json();

      if (data.success && data.product) {
        let images = [];
        if (data.product.images && Array.isArray(data.product.images) && data.product.images.length > 0) {
          images = [...data.product.images];
        } else if (data.product.image && data.product.image !== '0') {
          images = [data.product.image];
        }

        setProduct({
          id: data.product.id,
          name: data.product.name || '',
          description: data.product.description || '',
          price: data.product.price || '',
          sale_price: data.product.sale_price || '',
          category: data.product.category || 'electronics',
          stock: data.product.stock !== null && data.product.stock !== undefined
            ? data.product.stock
            : '',
          status: data.product.status || 'active',
          low_stock_threshold: data.product.low_stock_threshold !== null && data.product.low_stock_threshold !== undefined
            ? data.product.low_stock_threshold
            : '5',
          images: images,
          featured: data.product.featured || 0,
          is_new_arrival: Number(data.product.is_new_arrival) || 0,
          is_on_sale: Number(data.product.is_on_sale) || 0,
          product_details_title: data.product.product_details_title || 'Product Details',
          product_details: data.product.product_details || '',
          fabric_care_title: data.product.fabric_care_title || 'Fabric & Care',
          fabric_care: data.product.fabric_care || '',
          delivery_info_title: data.product.delivery_info_title || 'Delivery Information',
          delivery_info: data.product.delivery_info || '',
          return_policy_title: data.product.return_policy_title || 'Return & Exchange Policy',
          return_policy: data.product.return_policy || '',
          slider_badge: data.product.slider_badge || '',
        });
      } else {
        toast.error('Product not found');
        navigate('/admin/products');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const addGuideRow = () => {
    setGuide(prev => ({
      ...prev,
      guide_rows: [...prev.guide_rows, { size: '' }]
    }));
  };

  const updateGuideRow = (index, field, value) => {
    setGuide(prev => {
      const rows = [...prev.guide_rows];
      rows[index] = { ...rows[index], [field]: value };
      return { ...prev, guide_rows: rows };
    });
  };

  const removeGuideRow = (index) => {
    setGuide(prev => ({
      ...prev,
      guide_rows: prev.guide_rows.filter((_, i) => i !== index)
    }));
  };

  const handleGuideImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    setUploadingGuideImage(true);
    try {
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/admin/upload-guide-image.php', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        setGuide(prev => ({ ...prev, guide_image: data.image_url }));
        toast.success('Guide image uploaded!');
      } else {
        toast.error(data.message || 'Upload failed');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setUploadingGuideImage(false);
      e.target.value = '';
    }
  };

  const handleSaveGuide = async () => {
    setSavingGuide(true);
    try {
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/admin/save-product-guide.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: id, ...guide })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Guide saved!');
      } else {
        toast.error(data.message || 'Failed to save guide');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setSavingGuide(false);
    }
  };

  const loadVariants = async () => {
    try {
      const response = await fetch(`https://demo-site.storii.com.pk/demo-api/admin/get-product-variants.php?product_id=${id}`);
      const data = await response.json();
      if (data.success && data.variants) {
        setVariants(data.variants);
        if (data.variants.length > 0) {
          setShowVariants(true);
        }
      }
    } catch (error) {
      console.error('Error loading variants:', error);
    }
  };

  const handleVariantChange = (index, field, value) => {
    const updatedVariants = [...variants];
    updatedVariants[index][field] = value;
    setVariants(updatedVariants);
  };

  const handleVariantImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    setUploadingVariantImage(prev => ({ ...prev, [index]: true }));

    try {
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/admin/upload-variant-image.php', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        const updatedVariants = [...variants];
        updatedVariants[index].image = data.image_url;
        setVariants(updatedVariants);
        toast.success('Variant image uploaded!');
      } else {
        toast.error(data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Connection error');
    } finally {
      setUploadingVariantImage(prev => ({ ...prev, [index]: false }));
    }
  };

  const addVariant = () => {
    setVariants([
      ...variants,
      {
        id: Date.now(),
        sku: '',
        size: '',
        color: '',
        price: '',
        stock: '',
        image: ''
      }
    ]);
  };

  const removeVariant = (index) => {
    const updatedVariants = [...variants];
    updatedVariants.splice(index, 1);
    setVariants(updatedVariants);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (product.images.length + files.length > 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }

    const formData = new FormData();
    files.forEach(file => {
      formData.append('images[]', file);
    });

    setUploading(true);

    try {
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/admin/upload-product-images.php', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setProduct({ ...product, images: [...product.images, ...data.images] });
        toast.success(`${data.images.length} images uploaded successfully`);
      } else {
        toast.error(data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Connection error');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    const newImages = [...product.images];
    newImages.splice(index, 1);
    setProduct({ ...product, images: newImages });
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? (e.target.checked ? 1 : 0) : e.target.value;
    setProduct({ ...product, [e.target.name]: value });
  };

  const isStockLow = () => {
    const currentStock = parseInt(product.stock);
    const threshold = parseInt(product.low_stock_threshold);
    return !isNaN(currentStock) && !isNaN(threshold) && currentStock <= threshold && currentStock > 0;
  };

  const isOutOfStock = () => {
    const currentStock = parseInt(product.stock);
    return !isNaN(currentStock) && currentStock === 0;
  };

  const handleSaveSliderSettings = async () => {
    setSavingSlider(true);
    try {
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/admin/update-slider-settings.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: sliderTitle,
          subtitle: sliderSubtitle
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Slider title updated!');
        window.dispatchEvent(new Event('settingsUpdated'));
      } else {
        toast.error(data.message || 'Failed to update slider settings');
      }
    } catch (error) {
      console.error('Save slider settings error:', error);
      toast.error('Connection error');
    } finally {
      setSavingSlider(false);
    }
  };

  const handleClose = () => {
    navigate('/admin/products');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const lowStockThreshold = parseInt(product.low_stock_threshold);
    if (isNaN(lowStockThreshold) || lowStockThreshold < 0) {
      toast.error('Low stock threshold must be a valid positive number');
      setSubmitting(false);
      return;
    }

    // Guard against ever submitting an accidentally-empty images array —
    // if state somehow lost its images (stale load, race condition, etc.),
    // refuse to save rather than silently wiping out existing product images.
    if (!product.images || product.images.length === 0) {
      toast.error('No product images found — please add at least one image before saving.');
      setSubmitting(false);
      return;
    }

    const imagesJson = JSON.stringify(product.images);
    console.log('DEBUG - product.images before save:', product.images);
    console.log('DEBUG - imagesJson being sent:', imagesJson);

    const mainImage = product.images[0];

    const stockNum = parseInt(product.stock, 10);
    const computedStatus =
      !isNaN(stockNum) && stockNum === 0
        ? 'out_of_stock'
        : product.status === 'draft'
          ? 'draft'
          : 'active';

    const submitData = {
      id: product.id,
      name: product.name,
      description: product.description,
      price: product.price,
      sale_price: product.sale_price,
      category: product.category,
      stock: product.stock,
      status: computedStatus,
      low_stock_threshold: product.low_stock_threshold,
      image: mainImage,
      images: imagesJson,
      featured: product.featured,
      product_details_title: product.product_details_title,
      product_details: product.product_details,
      fabric_care_title: product.fabric_care_title,
      fabric_care: product.fabric_care,
      delivery_info_title: product.delivery_info_title,
      delivery_info: product.delivery_info,
      return_policy_title: product.return_policy_title,
      return_policy: product.return_policy,
      is_new_arrival: product.is_new_arrival,
      is_on_sale: product.is_on_sale,
      slider_badge: product.slider_badge,
    };

    try {
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/admin/update-product-no-auth.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (data.success) {
        if (variants.length > 0) {
          const variantsResponse = await fetch('https://demo-site.storii.com.pk/demo-api/admin/save-product-variants.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              product_id: product.id,
              variants: variants.map(v => ({
                sku: v.sku,
                size: v.size,
                color: v.color,
                price: v.price,
                stock: v.stock,
                image: v.image
              }))
            })
          });
          await variantsResponse.json();
        }

        toast.success('Product updated successfully!');
        setTimeout(() => navigate('/admin/products'), 1500);
      } else {
        toast.error(data.message || 'Failed to update product');
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Connection error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="edit-product-overlay">
      <div className="edit-product-backdrop" onClick={handleClose}></div>

      <div className="edit-product-drawer">
        <div className="drawer-header">
          <h2>Edit Product</h2>
          <button type="button" className="drawer-close-btn" onClick={handleClose}>
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="drawer-form">
          <div className="drawer-body">

            <section className="drawer-section">
              <h3 className="drawer-section-title">Basic Details</h3>

              <div className="form-group">
                <label>Product Name</label>
                <input type="text" name="name" value={product.name} onChange={handleChange} required />
              </div>

              <div className="form-group">
                <label>Category</label>
                <select name="category" value={product.category} onChange={handleChange} required>
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>
                      {categoryLabels[cat] || cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Product Description</label>
                <textarea name="description" value={product.description} onChange={handleChange} rows="4" required />
              </div>
            </section>

            <section className="drawer-section">
              <div className="drawer-row">
                <div className="form-group">
                  <label>Price (PKR)</label>
                  <input type="number" name="price" value={product.price} onChange={handleChange} step="0.01" required />
                </div>
                <div className="form-group">
                  <label>Sale Price (PKR)</label>
                  <input type="number" name="sale_price" value={product.sale_price} onChange={handleChange} placeholder="Optional" />
                </div>
              </div>

              <div className="drawer-row">
                <div className="form-group">
                  <label>Stock Quantity</label>
                  <input
                    type="number"
                    name="stock"
                    value={product.stock}
                    onChange={handleChange}
                    required
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Product Status</label>
                  <select
                    name="status"
                    value={
                      parseInt(product.stock, 10) === 0
                        ? 'out_of_stock'
                        : product.status
                    }
                    onChange={handleChange}
                    disabled={parseInt(product.stock, 10) === 0}
                  >
                    {parseInt(product.stock, 10) === 0 && (
                      <option value="out_of_stock">Out of Stock (auto)</option>
                    )}
                    <option value="active">Active</option>
                    <option value="draft">Draft</option>
                  </select>
                  <small>
                    {parseInt(product.stock, 10) === 0
                      ? 'Stock is 0 — this product will be marked Out of Stock on save.'
                      : 'Active products are visible on your store. Draft products stay hidden.'}
                  </small>
                </div>
              </div>

              {isStockLow() && (
                <div className="stock-warning">
                  <FaBell className="warning-icon" />
                  <span>Low stock alert! Current stock ({product.stock}) is below threshold ({product.low_stock_threshold})</span>
                </div>
              )}
              {isOutOfStock() && (
                <div className="stock-out">
                  <FaBell className="warning-icon" />
                  <span>Out of stock!</span>
                </div>
              )}

              <div className="form-group">
                <label>Low Stock Threshold</label>
                <input
                  type="number"
                  name="low_stock_threshold"
                  value={product.low_stock_threshold}
                  onChange={handleChange}
                  required
                  min="0"
                />
                <small>Alert when stock falls below this number</small>
              </div>
            </section>

            <section className="drawer-section homepage-display-section">
              <h3 className="drawer-section-title with-icon"><FaThLarge /> Homepage &amp; Store Display</h3>
              <p className="section-sub">Control how this product appears on the homepage and default system pages.</p>

              <label className="field-label">Product Tags</label>
              <div className="tag-toggle-row">
                <button
                  type="button"
                  className={`tag-toggle ${Number(product.is_new_arrival) === 1 ? 'active' : ''}`}
                  onClick={() => setProduct({ ...product, is_new_arrival: Number(product.is_new_arrival) === 1 ? 0 : 1 })}
                >
                  {Number(product.is_new_arrival) === 1 && <span className="tag-check">✓</span>} New Arrival
                </button>
                <button
                  type="button"
                  className={`tag-toggle ${Number(product.is_on_sale) === 1 ? 'active' : ''}`}
                  onClick={() => {
                    // Only block turning it ON — always allow turning it back off, even
                    // if sale_price is somehow empty by then.
                    if (Number(product.is_on_sale) !== 1 && (!product.sale_price || product.sale_price === '')) {
                      toast.error('Please add a Sale Price before marking this product as Sale.');
                      return;
                    }
                    setProduct({ ...product, is_on_sale: Number(product.is_on_sale) === 1 ? 0 : 1 });
                  }}
                >
                  {Number(product.is_on_sale) === 1 && <span className="tag-check">✓</span>} Sale
                </button>
              </div>
              <p className="tags-note-alert">** Please add a Sale Price before marking this product as Sale.</p>
              <p className="tags-note">Products tagged as New Arrival or Sale will appear on the default New Arrivals or Sale pages.</p>

              <div className="slider-toggle-row">
                <div>
                  <strong>Show in Homepage Product Slider</strong>
                  <p>Turn this on if you want this product to appear in the homepage product slider.</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    name="featured"
                    checked={product.featured === 1}
                    onChange={handleChange}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {/* 
              <div className="form-group">
                <label>Slider Badge</label>
                <select
                  name="slider_badge"
                  value={product.slider_badge}
                  onChange={handleChange}
                >
                  <option value="">No Badge</option>
                  <option value="New Arrival">New Arrival</option>
                  <option value="Best Seller">Best Seller</option>
                  <option value="Sale">Sale</option>
                  <option value="Limited">Limited</option>
                </select>
                <small className="ss-hint">Only shown if "Show in Homepage Product Slider" is turned on.</small>
              </div>
              */}

              {/* ============ Slider Section Header ============ */}
              {/* 
              <div className="slider-section-settings">
                <div className="slider-section-divider">
                  <span>Homepage Slider Section Header</span>
                </div>

                <p className="slider-section-hint">
                  This controls the title shown above the New Arrivals slider on the homepage.
                  It's a global setting — updating it here affects the whole section, not just this product.
                </p>

                <div className="form-group">
                  <label>Slider Title</label>
                  <input
                    type="text"
                    value={sliderTitle}
                    onChange={(e) => setSliderTitle(e.target.value)}
                    placeholder="e.g. New Arrivals"
                  />
                </div>

                <div className="form-group">
                  <label>Slider Subtitle</label>
                  <input
                    type="text"
                    value={sliderSubtitle}
                    onChange={(e) => setSliderSubtitle(e.target.value)}
                    placeholder="e.g. Fresh styles just added to the collection."
                  />
                </div>

                <button
                  type="button"
                  className="save-slider-btn"
                  onClick={handleSaveSliderSettings}
                  disabled={savingSlider}
                >
                  {savingSlider ? 'Saving...' : 'Update Slider Title'}
                </button>
              </div>
              */}
            </section>

            <div className="drawer-section">
              <h3 className="drawer-section-title">Product Information</h3>

              <div className="content-block">
                <div className="form-group">
                  <label>Section 1 — Header</label>
                  <input type="text" name="product_details_title" value={product.product_details_title} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Section 1 — Description</label>
                  <textarea rows="4" name="product_details" value={product.product_details} onChange={handleChange}
                    placeholder="Regular fit&#10;Premium fabric blend" />
                </div>
              </div>

              <div className="content-block">
                <div className="form-group">
                  <label>Section 2 — Header</label>
                  <input type="text" name="fabric_care_title" value={product.fabric_care_title} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Section 2 — Description</label>
                  <textarea rows="4" name="fabric_care" value={product.fabric_care} onChange={handleChange}
                    placeholder="Cotton blend fabric&#10;Machine wash cold" />
                </div>
              </div>

              <div className="content-block">
                <div className="form-group">
                  <label>Section 3 — Header</label>
                  <input type="text" name="delivery_info_title" value={product.delivery_info_title} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Section 3 — Description</label>
                  <textarea rows="3" name="delivery_info" value={product.delivery_info} onChange={handleChange}
                    placeholder="Standard delivery within 3–5 working days" />
                </div>
              </div>

              <div className="content-block">
                <div className="form-group">
                  <label>Section 4 — Header</label>
                  <input type="text" name="return_policy_title" value={product.return_policy_title} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Section 4 — Description</label>
                  <textarea rows="3" name="return_policy" value={product.return_policy} onChange={handleChange}
                    placeholder="Exchange available within 7 days" />
                </div>
              </div>
            </div>

            <section className="drawer-section homepage-display-section">
              <h3 className="drawer-section-title with-icon"> Size Guide / Product Guide</h3>
              <div className="variants-section">

                <p className="help-text">Add helpful sizing or buying guidance so customers can choose the right product.</p>
                {/* 
                <div className="form-group">
                  <label>Guide Type</label>
                  <select
                    value={guide.guide_type}
                    onChange={(e) => setGuide({ ...guide, guide_type: e.target.value })}
                  >
                    <option value="size_guide">Size Guide</option>
                    <option value="fit_guide">Fit Guide</option>
                    <option value="buying_guide">Buying Guide</option>
                  </select>
                </div>
                */}
                <div className="form-group">
                  <label>Guide Title</label>
                  <input
                    type="text"
                    value={guide.guide_title}
                    onChange={(e) => setGuide({ ...guide, guide_title: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Guide Description</label>
                  <textarea
                    rows="3"
                    value={guide.guide_description}
                    onChange={(e) => setGuide({ ...guide, guide_description: e.target.value })}
                    placeholder="Use this guide to choose the right size. Measurements may vary slightly depending on fabric and fit."
                  />
                </div>

                {/* 
                <label className="field-label">Available Sizes</label>
                {guide.guide_rows.map((row, index) => (
                  <div key={index} className="drawer-row guide-row-single">
                    <input
                      type="text"
                      placeholder="e.g. S, M, L, XL"
                      value={row.size}
                      onChange={(e) => updateGuideRow(index, 'size', e.target.value)}
                    />
                    <button type="button" className="remove-variant-btn" onClick={() => removeGuideRow(index)}><FaTrash /></button>
                  </div>
                ))}
                <button type="button" className="add-variant-btn" onClick={addGuideRow}>
                  <FaPlus /> Add Row
                </button>
                */}
                <label className="field-label">Available Sizes</label>
                <div className="sizes-grid">
                  {guide.guide_rows.map((row, index) => (
                    <div key={index} className="size-box">
                      <input
                        type="text"
                        className="size-input"
                        placeholder="S"
                        value={row.size}
                        onChange={(e) => updateGuideRow(index, 'size', e.target.value)}
                      />
                      <button
                        type="button"
                        className="size-remove-btn"
                        onClick={() => removeGuideRow(index)}
                        aria-label="Remove size"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                  <button type="button" className="add-size-box" onClick={addGuideRow}>
                    Add More<br />Size
                  </button>
                </div>

                {/* This block was accidentally dropped when the show/hide toggle was
    removed — handleGuideImageUpload/uploadingGuideImage were still
    defined but had nothing rendering them. */}
                <div className="form-group" style={{ marginTop: '14px' }}>
                  <label>Size Guide Image</label>
                  {guide.guide_image && (
                    <div className="variant-image-preview">
                      <img src={guide.guide_image} alt="Size guide" />
                      <button type="button" className="remove-variant-img" onClick={() => setGuide({ ...guide, guide_image: '' })}>✕</button>
                    </div>
                  )}
                  <input
                    type="file"
                    id="guideImageUpload"
                    accept="image/*"
                    onChange={handleGuideImageUpload}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="upload-variant-btn"
                    onClick={() => document.getElementById('guideImageUpload').click()}
                    disabled={uploadingGuideImage}
                  >
                    <FaUpload /> {uploadingGuideImage ? 'Uploading...' : 'Upload Size Guide Image'}
                  </button>
                </div>

                <div className="slider-toggle-row"></div>

                <div className="slider-toggle-row">
                  <div>
                    <strong>Show Guide on Product Page</strong>
                    <p>Turn this on to show the guide on the product detail page.</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={guide.show_on_page === 1}
                      onChange={(e) => setGuide({ ...guide, show_on_page: e.target.checked ? 1 : 0 })}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <button type="button" className="upload-variant-btn" onClick={handleSaveGuide} disabled={savingGuide} style={{ marginTop: '14px' }}>
                  {savingGuide ? 'Saving...' : 'Save Guide'}
                </button>
              </div>
            </section>

            {/*
            <section className="drawer-section">
              <button
                type="button"
                className="toggle-variants-btn"
                onClick={() => setShowVariants(!showVariants)}
              >
                <FaCopy /> {showVariants ? 'Hide Variants' : 'Manage Variants (Size, Color, etc.)'}
              </button>
              {variants.length > 0 && !showVariants && (
                <div className="variants-summary">
                  <small>{variants.length} variant(s) configured</small>
                </div>
              )}

              {showVariants && (
                <div className="variants-section">
                  <p className="help-text">Manage different sizes, colors, or variations with their own price, stock, and image</p>

                  {variants.map((variant, index) => (
                    <div key={variant.id || index} className="variant-card">
                      <div className="variant-header">
                        <h4>Variant {index + 1}</h4>
                        <button type="button" className="remove-variant-btn" onClick={() => removeVariant(index)}>
                          <FaTrash /> Remove
                        </button>
                      </div>
                      <div className="drawer-row">
                        <div className="form-group">
                          <label>SKU</label>
                          <input
                            type="text"
                            placeholder="SKU-001"
                            value={variant.sku || ''}
                            onChange={(e) => handleVariantChange(index, 'sku', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label>Size</label>
                          <input
                            type="text"
                            placeholder="S, M, L, XL"
                            value={variant.size || ''}
                            onChange={(e) => handleVariantChange(index, 'size', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="drawer-row">
                        <div className="form-group">
                          <label>Price</label>
                          <input
                            type="number"
                            placeholder="Price"
                            step="0.01"
                            value={variant.price || ''}
                            onChange={(e) => handleVariantChange(index, 'price', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Stock</label>
                        <input
                          type="number"
                          placeholder="Stock"
                          min="0"
                          value={variant.stock || ''}
                          onChange={(e) => handleVariantChange(index, 'stock', e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label>Variant Image</label>
                        {variant.image && (
                          <div className="variant-image-preview">
                            <img src={variant.image} alt="Variant" />
                            <button
                              type="button"
                              className="remove-variant-img"
                              onClick={() => handleVariantChange(index, 'image', '')}
                            >
                              ✕
                            </button>
                          </div>
                        )}
                        <div className="variant-upload-area">
                          <input
                            type="file"
                            id={`variant_image_${index}`}
                            accept="image/*"
                            onChange={(e) => handleVariantImageUpload(e, index)}
                            style={{ display: 'none' }}
                          />
                          <button
                            type="button"
                            className="upload-variant-btn"
                            onClick={() => document.getElementById(`variant_image_${index}`).click()}
                            disabled={uploadingVariantImage[index]}
                          >
                            <FaUpload /> {uploadingVariantImage[index] ? 'Uploading...' : 'Upload Variant Image'}
                          </button>
                          <small>Optional image for this specific variant</small>
                        </div>
                      </div>
                    </div>
                  ))}

                  <button type="button" className="add-variant-btn" onClick={addVariant}>
                    <FaPlus /> Add Another Variant
                  </button>
                </div>
              )}
            </section>
            */}

            <section className="drawer-section">
              <label className="field-label">Product Images (Max 5)</label>
              <div className="multi-image-upload">
                <div className="image-preview-grid">
                  {product.images.map((img, index) => (
                    <div key={index} className="image-preview-item">
                      <img src={img} alt={`Preview ${index}`} />
                      <button type="button" className="remove-image-btn" onClick={() => removeImage(index)}>
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                  {product.images.length < 5 && (
                    <div className="image-upload-box">
                      <input
                        type="file"
                        id="productImages"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                        disabled={uploading}
                      />
                      <button
                        type="button"
                        className="upload-images-btn"
                        onClick={() => document.getElementById('productImages').click()}
                        disabled={uploading}
                      >
                        <FaImage /> {uploading ? 'Uploading...' : 'Add More Images'}
                      </button>
                    </div>
                  )}
                </div>
                <p className="help-text">Upload up to 5 images. First image is the main product image.</p>
              </div>
            </section>
          </div>

          <div className="drawer-footer">
            <button type="button" onClick={handleClose} className="cancel-btn">
              Cancel
            </button>
            <button type="button" className="draft-btn" disabled title="Not wired up yet">
              Save as Draft
            </button>
            <button type="submit" disabled={submitting} className="submit-btn">
              {submitting ? 'Updating...' : 'Update Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProduct;